import { Component, OnInit } from '@angular/core';
import { FenceService } from '../../../../services/configurators-2d/applications/fence.service';
import { TranslateService } from '@ngx-translate/core';
import { Tool } from '../../../../models/configurators-2d/applications/fence/tool';
import { FenceConfiguratorItem } from '../../configurator/applications/fence-configurator-item';
import { Accessory } from 'src/app/models/configurators-2d/applications/fence/accessory';
import { IBaseComponent } from '../../configurator/i-base-component';
import { checkPanelCount, remainingDoorNumber } from 'src/app/services/helpers/fence-helper';
import { MaterialsFacadeService } from '../materials/services/materials.facade.service';
import { ColorStateService } from '../services/color.state.service';
import { environment } from 'src/environments/environment';
import { ClosetDoorsCustomPreviewComponent } from '../../closet-doors/custom/preview/preview.component';
import { Configurators2DComponent } from '../../configurators-2d.component';

@Component({
  selector: 'ng-fence-accessories',
  templateUrl: './accessories.component.html',
})
export class FenceAccessoriesComponent extends FenceConfiguratorItem implements OnInit, IBaseComponent {
  /** URL de base des images */
  baseImgURL = undefined;

  /** Langue sélectionnée */
  currentLang: string;

  /** Liste des outils */
  tools: Tool[];

  preview: ClosetDoorsCustomPreviewComponent;

  accessories: Accessory[];

  /** Sous-étape */
  subStep: number = 2;

  isVendor!: boolean;

  constructor(
    fenceService: FenceService,
    private materialsFacadeService: MaterialsFacadeService,
    private translate: TranslateService,
    private colorStateService: ColorStateService,
    private configurators2DComponent: Configurators2DComponent
  ) {
    super(fenceService);
    this.baseImgURL = this.service.apiUrl;
    this.isVendor = environment.vendor;
    this.preview = configurators2DComponent.preview as ClosetDoorsCustomPreviewComponent;
  }

  /**
   * Check si une couleur est sélectionnée et n'est pas désactivée
   * Sauf si c'est un vendeur, on le laisse continuer la configuration malgré le déréférencement
   * @private
   */
  private get checkColorSelectionContainDisabled(): boolean {
    if (this.isVendor) {
      return false;
    }

    for (const configurationInstallation of this.configuration.configurationInstallations) {
      const colorIds = configurationInstallation.configurationBoards.map(b => b.boardId);

      const colors = this.colorStateService
        .colorsByInstallations()
        .get(configurationInstallation.installationId)
        .filter(c => colorIds.includes(c.id));

      if (colors.some(c => c.isDisabled)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check si un poteau est sélectionné et n'est pas désactivé
   * Sauf si c'est un vendeur, on le laisse continuer la configuration malgré le déréférencement
   * @private
   */
  private get checkPostTypeSelectionIsCorrect(): boolean {
    return this.configuration.postType && (this.isVendor || !this.configuration.postType.isDisabled);
  }

  /**
   * Check si tous les portillons sélectionnés ne sont pas désactivés et que tous les emplacements de portillon sont pris.
   * Sauf si c'est un vendeur, on le laisse continuer la configuration malgré le déréférencement
   * @private
   */
  private get checkDoorsSelectionIsCorrect(): boolean {
    return (
      remainingDoorNumber(this.configuration) === 0 &&
      this.configuration.doors != undefined &&
      (this.isVendor || this.configuration.doors.every(d => !d.isDisabled))
    );
  }

  ngOnInit(): void {
    this.currentLang = this.translate.getBrowserLang();

    this.service.getAccessories(this.configuration.location.id).then(data => {
      this.tools = data.supplies;
      this.accessories = data.accessories;
    });

    this.preview.update().then();
  }

  /**
   * Fonction d'affichage des sous menus
   * @param step sous étape de la page finition
   */
  toggleCollapse(step: number) {
    if (this.subStep !== step) {
      this.subStep = step;
    } else {
      this.subStep = 0;
    }
  }

  isNextButtonActive(): boolean {
    return this.checkPageChangeCapability();
  }

  checkPageChangeCapability(): boolean {
    const checkDecorativeElementsCompleted = this.materialsFacadeService.checkDecorativeElementsCompleted(this.configuration);

    return (
      this.configuration.configurationInstallations.length > 0 &&
      this.configuration.configurationInstallations.every(i => i.configurationBoards.length > 0) &&
      !this.checkColorSelectionContainDisabled &&
      this.checkPostTypeSelectionIsCorrect &&
      checkDecorativeElementsCompleted &&
      this.checkDoorsSelectionIsCorrect &&
      checkPanelCount(this.configuration)
    );
  }
}
